import React, { useState, useEffect, useRef } from "react";
import { DBM, RELATIONSHIPS, RULE_FUNCTION_CATEGORY, RULES } from "../../ISRConstants";
import {
  RuleObjectStateType,
  RuleObjectType,
} from "../../Pages/DefineModel/model";
import {
  TConnector,
  TField,
  TIndexState,
  TProductCategory,
  TSubmodel,
  TUpsertISRProjectDataModelsInput,
} from "../../Pages/DefineModel/model";
import RulesCard from "./RulesCard";
import Typography from "Components/DesignSystem/Typography/Typography";
import ManageRulesDialog from "../../Pages/DefineModel/Components/ManageRulesDialog";
import CreateRuleMenu from "../CreateRuleMenu";
import { SB3RuleContextProvider } from "./SB3RuleContext";
import { useISRContext } from "../../Hooks/useISRContext";
import { mergeKeepingLocal } from "../../ISRHelper";
import { deleteRule, getFieldDBMRules } from "../../service";
import { toast } from "Components/DesignSystem/Toast/useToast";

type RulesComponentProps = {
  id: string;
  selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
  setUserProjectDataModels: React.Dispatch<
    React.SetStateAction<TUpsertISRProjectDataModelsInput>
  >;
  userProjectDataModels: TUpsertISRProjectDataModelsInput;
  indexInfo?: TIndexState;
  createISRProject:(isRuleUpdate: boolean)=>void;
  featureType: string
};

// type commandType = {
//   id: number;
//   label: string;
//   description: string;
//   hasOptions: boolean;
//   onClickHandler: Function;
//   child: [];
//   type: string;
//   choiceType: string;
// };

const RulesComponent = (
  props: React.PropsWithChildren<RulesComponentProps>
) => {
  const isrContext = useISRContext();
  // useEffect(() => {
  const [rules, setRules] = useState<RuleObjectStateType[]>([]);
  const [inputRef, setInputRef] = useState<any>();
  const [showRulesMenu, setShowRulesMenu] = useState<boolean>(false);
  const [editId, setEditId] = useState<any>(null);
  const [ruleType_, setRuleType_] = useState<string | null | undefined>(null);
  let {
    selectedItem,
    userProjectDataModels,
    setUserProjectDataModels,
    indexInfo,
    createISRProject
  } = props;

  
  

  const isCategoryFeature = props.featureType === RULE_FUNCTION_CATEGORY;

  // DBM rules are `isr_rules` documents (the Logic rule pattern), not entries in
  // the field's embedded `rules` array — fetched per Source field and listed after
  // the embedded ones. Only fields have them (the DBM option is hidden on categories).
  const [dbmRules, setDbmRules] = useState<RuleObjectStateType[]>([]);
  const dbmRequestRef = useRef(0);
  const projectId = userProjectDataModels?.project?.id;
  const dbmFieldId = isCategoryFeature ? undefined : (selectedItem as TField | null)?.id;

  const loadDbmRules = async () => {
    const requestId = ++dbmRequestRef.current;
    if (!projectId || !dbmFieldId) {
      setDbmRules([]);
      return;
    }
    const res = await getFieldDBMRules(projectId, dbmFieldId);
    // Switching fields while a request is in flight must not show the old field's rules.
    if (requestId !== dbmRequestRef.current) return;
    const docs: { id: string; name: string; description?: string }[] = res?.data?.data ?? [];
    setDbmRules(docs.map((doc) => ({ id: doc.id, name: doc.name, description: doc.description, type: DBM })));
  };

  useEffect(() => {
    loadDbmRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, dbmFieldId]);

  useEffect(() => {
    let prevRules;
    const categoryItem = selectedItem as TProductCategory;
    const belongsToDataModel = isCategoryFeature
      ? !!categoryItem?.data?.belongsToDataModel
      : !!(
          selectedItem &&
          "belongsToDataModel" in selectedItem &&
          selectedItem?.belongsToDataModel
        );
    if (belongsToDataModel) {
      prevRules = isCategoryFeature
        ? userProjectDataModels?.project.dataModels[
            indexInfo?.dataModelIndex ?? -1
          ]?.data.productCategories[indexInfo?.categoryIndex ?? -1]?.data
            ?.rules ?? []
        : userProjectDataModels?.project.dataModels[
            indexInfo?.dataModelIndex ?? -1
          ]?.data.fields[indexInfo?.fieldIndex ?? -1]?.rules ?? [];
    } else if (isCategoryFeature) {
      prevRules =
        userProjectDataModels?.project.productCategories[
          indexInfo?.categoryOutsideDataModelIndex ?? -1
        ]?.data?.rules ?? [];
    } else {
      prevRules =
        userProjectDataModels?.project.fields[
          indexInfo?.fieldOutsideDataModelIndex ?? -1
        ]?.data?.rules ?? [];
    }
    if (Array.isArray(prevRules)) {
      setRules(prevRules);
      // Only auto-default editId to the last rule while the dialog is closed. While
      // ManageRulesDialog is open (showRulesMenu), editId is owned by the explicit
      // Edit-click / "+ Create" click that opened it (handleClick / CreateRuleMenu's
      // handleOptionClick). Any incidental userProjectDataModels refresh while editing
      // (e.g. the save this same dialog just triggered, or an unrelated autosave
      // elsewhere in the tree) used to re-run this effect and silently reassign editId
      // to whatever rule is now last in the array, hijacking the open dialog to edit a
      // different rule than the one on screen — see okf/isr/e2e.md.
      if (!showRulesMenu) {
        setEditId(prevRules[prevRules.length - 1]?.id);
      }
    }
  }, [selectedItem, userProjectDataModels]);

  // useEffect(() => {
  //   scrollToBottom();
  // });

  // const scrollToBottom = () => {
    //if (inputRef) inputRef.scrollIntoView({ behavior: "smooth" });
  // };

  const updateRules = (localRules: RuleObjectStateType[]) => {
    setUserProjectDataModels((prevDataModels) => {
      const newDataModels = { ...prevDataModels };
      const newRules: RuleObjectType[] = localRules.map((x) => {
        return {
          id: x.id,
          ruleExp: x.ruleExp,
          ruleScope: x.ruleScope ?? {},
          name: x.name,
          description: x.description ?? "",
          type: x.type,
        };
      });
      const categoryItem = selectedItem as TProductCategory;
      const belongsToDataModel = isCategoryFeature
        ? !!categoryItem?.data?.belongsToDataModel
        : !!(
            selectedItem &&
            "belongsToDataModel" in selectedItem &&
            selectedItem?.belongsToDataModel
          );

      if (belongsToDataModel) {
        const dataModelToUpdate =
          newDataModels.project.dataModels[indexInfo?.dataModelIndex ?? -1];
        if (isCategoryFeature) {
          const categoryToUpdate =
            dataModelToUpdate?.data?.productCategories?.[
              indexInfo?.categoryIndex ?? -1
            ];
          if (categoryToUpdate && categoryToUpdate.data) {
            categoryToUpdate.data.rules = newRules;
          }
        } else if (
          dataModelToUpdate &&
          dataModelToUpdate.data &&
          dataModelToUpdate.data.fields[indexInfo?.fieldIndex ?? -1]
        ) {
          dataModelToUpdate.data.fields[
            indexInfo?.fieldIndex ?? -1
          ].rules = newRules;
        }
      } else if (isCategoryFeature) {
        const categoryToUpdate =
          newDataModels.project.productCategories[
            indexInfo?.categoryOutsideDataModelIndex ?? -1
          ];
        if (categoryToUpdate && categoryToUpdate.data) {
          categoryToUpdate.data.rules = newRules;
        }
      } else {
        const fieldToUpdate =
          newDataModels.project.fields[
            indexInfo?.fieldOutsideDataModelIndex ?? -1
          ];
        if (fieldToUpdate && fieldToUpdate.data) {
          fieldToUpdate.data.rules = newRules;
        }
      }
      return newDataModels;
    });
    createISRProject(true)
  };


  const handleClick = async (id: any, type: string, ruleType:string|null|undefined) => {
    if (type === "edit") {
      setEditId(id);
      setRuleType_(ruleType);
      setShowRulesMenu((prev) => !prev);
    } else if (dbmRules.some((rule) => rule.id === id)) {
      // Same as the Logic Rules page's delete.
      if (await deleteRule(id)) {
        toast.success("Deleted successfully");
        setDbmRules((prev) => prev.filter((rule) => rule.id !== id));
      } else {
        toast.warning("Delete Unsuccessful");
      }
    } else {
    let localRules = [...rules];
    localRules = rules.filter((x) => x.id !== id);
    setRules(localRules);
    updateRules(localRules)
    }
  };

  return (
    <SB3RuleContextProvider>

    {/* The legacy cart-style global expanded at the call site onto role tokens
        (it hardcodes white/#e5e5ea and must never be re-skinned globally). */}
    <div id={props.id} className="tw-rounded tw-bg-surface tw-box-border tw-w-[98%] tw-h-auto tw-border tw-border-solid tw-border-edge tw-py-2 tw-px-4" ref={(ele) => setInputRef(ele)}>
      <div className="tw-flex tw-justify-between tw-items-center ">
        <Typography variant="sub-title-label" as="h2" className="tw-text-ink-secondary">
          {RELATIONSHIPS}
        </Typography>
        <CreateRuleMenu
          handleOptionClick ={
            (type: string) => {
            setRuleType_(type);
            setShowRulesMenu((prev) => !prev);
              setEditId(null);
          }}
            mergeStates={() => {
              setUserProjectDataModels((prevDataModels) =>
                mergeKeepingLocal(prevDataModels, isrContext?.networkUserProjectDataModels)
              );
            }}
            hideCbmOption={props.featureType === RULE_FUNCTION_CATEGORY}
        />
      </div>
      {[...rules, ...dbmRules].map((rule,ruleIndex ) => (
          <div key={ruleIndex}>
            <RulesCard
            id={rule.id}
            ruleName={rule.name}
            description={rule.description}
            ruleType={rule.type}
            ruleExp={rule.ruleExp}
            onClickHandler={(id: any, type: string) => {
              handleClick(id, type, rule.type);
            }}
          />
          
          </div>

        ))}
      {showRulesMenu && (
          <ManageRulesDialog
            showRulesMenu={showRulesMenu}
            setShowRulesMenu={setShowRulesMenu}
            selectedItem={selectedItem}
            userProjectDataModels={userProjectDataModels}
            setUserProjectDataModels={setUserProjectDataModels}
            indexInfo={indexInfo}
            editId={editId}
            ruleType={ruleType_}
            createISRProject={createISRProject}
            featureType={props?.featureType}
            onDbmRuleSaved={loadDbmRules}
          />

      )}
    </div>
    </SB3RuleContextProvider>

  );
};

export default RulesComponent;
